import { createFileRoute } from '@tanstack/react-router';
import { doNothing } from 'remeda';

import { client } from '@@api/client';
import { writeLastRoutePathToStorage } from '@@containers/LastRouteTracker/utils';
import { useSettingsStore } from '@@scripts/store/settings/useSettingsStore';
import { getIsAuthenticated, useAuthStore } from '@@scripts/store/useAuthStore';

export const Route = createFileRoute('/_authenticated')({
    beforeLoad: async ({ cause, context, location }) => {
        const { userId } = useAuthStore.getState();

        // The preferences come from the server, not the token, so a change made in another tab or
        // device applies on the next load.
        if (getIsAuthenticated() && cause === 'enter' && userId) {
            const preferences = context.queryClient
                .query(
                    // Pages may wait for them, and the app can start without them, offline too
                    client.auth.users.preference.get.queryOptions(
                        { params: { id: userId } },
                        { retry: false, networkMode: 'always' },
                    ),
                )
                .then(({ body }) =>
                    useSettingsStore.getState().hydrateSettings({
                        contentLocale: body.contentLocale,
                        betaFeaturesEnabled: body.betaFeaturesEnabled,
                        collapsing: body.collapsing,
                        filters: body.filters,
                        uiLocale: body.uiLocale,
                        preferencesUserId: userId,
                    }),
                )
                // The app goes on with the settings it kept from the last session. A 401 signed the
                // user out, which the check below handles.
                .catch(doNothing());

            // Loaders build their query keys from the content language and restore the saved filters,
            // so they wait unless the store keeps this user's preferences. Kept ones are refreshed
            // when the server answers, and a new content language reloads the page.
            if (useSettingsStore.getState().preferencesUserId !== userId) {
                await preferences;
            }
        }

        if (getIsAuthenticated()) {
            return;
        }

        // In order to avoid using acceptance environments when not necessary
        // We revert to default ones when token expires
        if (useAuthStore.getState().accessToken) {
            localStorage.removeItem('CDII_API');
        }

        // The login page sends the user back to the page they asked for
        writeLastRoutePathToStorage(location.href);

        throw await context.getLoginRedirect();
    },
});
